import { Link } from 'react-router-dom';
import { Button } from '../components/ui';
import {
  MagnifyingGlassIcon,
  DocumentTextIcon,
  CurrencyDollarIcon,
  ClockIcon,
  CheckCircleIcon
} from '@heroicons/react/24/outline';

export default function ProductPage() {
  return (
    <div className="min-h-screen bg-gray-50">
      {/* Hero Section */}
      <section className="bg-white border-b border-gray-200">
        <div className="container mx-auto px-4 py-16 md:py-24">
          <div className="max-w-4xl mx-auto text-center">
            <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
              Electrical Permit Fees, Instantly
            </h1>
            <p className="text-xl text-gray-600 mb-8">
              PermitBuddy helps electrical contractors calculate permit fees in seconds,
              not hours. Stop searching through municipal websites and get accurate fee
              estimates for your jobs instantly.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link to="/register">
                <Button variant="primary" size="lg" className="w-full sm:w-auto px-8">
                  Get Started Free
                </Button>
              </Link>
              <Link to="/permits/search">
                <Button variant="secondary" size="lg" className="w-full sm:w-auto px-8">
                  Try Demo
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-16 md:py-24">
        <div className="container mx-auto px-4">
          <div className="max-w-6xl mx-auto">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 text-center mb-12">
              Features Built for Contractors
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
              {/* Feature 1 */}
              <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
                <div className="w-12 h-12 bg-primary-50 rounded-lg flex items-center justify-center mb-4">
                  <MagnifyingGlassIcon className="w-6 h-6 text-primary-600" />
                </div>
                <h3 className="text-lg font-semibold text-gray-900 mb-2">
                  Smart Municipality Search
                </h3>
                <p className="text-gray-600">
                  Automatically finds the right permit portal for any city and state.
                  No more manual searching.
                </p>
              </div>

              {/* Feature 2 */}
              <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
                <div className="w-12 h-12 bg-primary-50 rounded-lg flex items-center justify-center mb-4">
                  <DocumentTextIcon className="w-6 h-6 text-primary-600" />
                </div>
                <h3 className="text-lg font-semibold text-gray-900 mb-2">
                  Requirements Extraction
                </h3>
                <p className="text-gray-600">
                  Get all permit requirements, application steps, and necessary
                  documentation in one place.
                </p>
              </div>

              {/* Feature 3 */}
              <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
                <div className="w-12 h-12 bg-primary-50 rounded-lg flex items-center justify-center mb-4">
                  <CurrencyDollarIcon className="w-6 h-6 text-primary-600" />
                </div>
                <h3 className="text-lg font-semibold text-gray-900 mb-2">
                  Accurate Fee Calculation
                </h3>
                <p className="text-gray-600">
                  Instant permit fee estimates based on your specific job details.
                  Know costs before you bid.
                </p>
              </div>

              {/* Feature 4 */}
              <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
                <div className="w-12 h-12 bg-primary-50 rounded-lg flex items-center justify-center mb-4">
                  <ClockIcon className="w-6 h-6 text-primary-600" />
                </div>
                <h3 className="text-lg font-semibold text-gray-900 mb-2">
                  Save Hours of Research
                </h3>
                <p className="text-gray-600">
                  Get results in seconds instead of spending hours navigating
                  complex municipal websites.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* How It Works */}
      <section className="py-16 md:py-24 bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 text-center mb-12">
              How It Works
            </h2>
            <div className="space-y-8">
              <div className="flex gap-4">
                <div className="flex-shrink-0 w-10 h-10 bg-primary-600 text-white rounded-full flex items-center justify-center font-bold">
                  1
                </div>
                <div>
                  <h3 className="text-xl font-semibold text-gray-900 mb-2">
                    Enter Job Details
                  </h3>
                  <p className="text-gray-600">
                    Simply provide the city, state, and a brief description of your
                    electrical work (e.g., "residential panel upgrade", "commercial lighting").
                  </p>
                </div>
              </div>

              <div className="flex gap-4">
                <div className="flex-shrink-0 w-10 h-10 bg-primary-600 text-white rounded-full flex items-center justify-center font-bold">
                  2
                </div>
                <div>
                  <h3 className="text-xl font-semibold text-gray-900 mb-2">
                    We Find the Requirements
                  </h3>
                  <p className="text-gray-600">
                    Our system automatically discovers the municipality's permit portal,
                    extracts permit types, requirements, and fee structures.
                  </p>
                </div>
              </div>

              <div className="flex gap-4">
                <div className="flex-shrink-0 w-10 h-10 bg-primary-600 text-white rounded-full flex items-center justify-center font-bold">
                  3
                </div>
                <div>
                  <h3 className="text-xl font-semibold text-gray-900 mb-2">
                    Get Instant Results
                  </h3>
                  <p className="text-gray-600">
                    Receive accurate permit fees, required documentation, and
                    application instructions - all in seconds.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Pricing Section */}
      <section className="py-16 md:py-24">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 text-center mb-12">
              Simple Credit-Based Pricing
            </h2>

            <div className="bg-white rounded-lg shadow-lg border border-gray-200 p-8 md:p-12">
              <div className="text-center mb-8">
                <div className="text-5xl font-bold text-gray-900 mb-2">
                  1 credit
                  <span className="text-2xl text-gray-600 font-normal"> per search</span>
                </div>
                <p className="text-gray-600">
                  Buy credit packages and only pay for what you use. No subscriptions, no commitments.
                </p>
                <div className="mt-4 grid grid-cols-2 md:grid-cols-4 gap-3 text-sm">
                  <div className="bg-gray-50 rounded-lg p-3">
                    <div className="font-bold text-gray-900">20 credits</div>
                    <div className="text-gray-600">$20 ($1.00/ea)</div>
                  </div>
                  <div className="bg-indigo-50 rounded-lg p-3 ring-2 ring-indigo-500">
                    <div className="font-bold text-gray-900">50 credits</div>
                    <div className="text-gray-600">$40 ($0.80/ea)</div>
                    <div className="text-xs text-indigo-600 font-medium">Save 20%</div>
                  </div>
                  <div className="bg-gray-50 rounded-lg p-3">
                    <div className="font-bold text-gray-900">100 credits</div>
                    <div className="text-gray-600">$75 ($0.75/ea)</div>
                    <div className="text-xs text-green-600 font-medium">Save 25%</div>
                  </div>
                  <div className="bg-gray-50 rounded-lg p-3">
                    <div className="font-bold text-gray-900">200 credits</div>
                    <div className="text-gray-600">$125 ($0.63/ea)</div>
                    <div className="text-xs text-green-600 font-medium">Save 37.5%</div>
                  </div>
                </div>
              </div>

              <div className="space-y-4 mb-8">
                <div className="flex items-start gap-3">
                  <CheckCircleIcon className="w-6 h-6 text-green-600 flex-shrink-0 mt-0.5" />
                  <span className="text-gray-700">
                    Unlimited municipality coverage across the United States
                  </span>
                </div>
                <div className="flex items-start gap-3">
                  <CheckCircleIcon className="w-6 h-6 text-green-600 flex-shrink-0 mt-0.5" />
                  <span className="text-gray-700">
                    Accurate permit fee calculations for your specific jobs
                  </span>
                </div>
                <div className="flex items-start gap-3">
                  <CheckCircleIcon className="w-6 h-6 text-green-600 flex-shrink-0 mt-0.5" />
                  <span className="text-gray-700">
                    Complete permit requirements and application instructions
                  </span>
                </div>
                <div className="flex items-start gap-3">
                  <CheckCircleIcon className="w-6 h-6 text-green-600 flex-shrink-0 mt-0.5" />
                  <span className="text-gray-700">
                    Instant results - get your answer in seconds
                  </span>
                </div>
                <div className="flex items-start gap-3">
                  <CheckCircleIcon className="w-6 h-6 text-green-600 flex-shrink-0 mt-0.5" />
                  <span className="text-gray-700">
                    Search history and saved results in your dashboard
                  </span>
                </div>
              </div>

              <div className="text-center">
                <Link to="/register">
                  <Button variant="primary" size="lg" className="px-8">
                    Start Searching Now
                  </Button>
                </Link>
                <p className="text-sm text-gray-600 mt-4">
                  Sign up free. Add credits when you need them.
                </p>
              </div>
            </div>

            <div className="mt-8 text-center text-gray-600">
              <p className="text-sm">
                Need bulk pricing or enterprise features?
                <Link to="/contact" className="text-primary-600 hover:text-primary-700 ml-1">
                  Contact us
                </Link>
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 md:py-24 bg-primary">
        <div className="container mx-auto px-4">
          <div className="max-w-3xl mx-auto text-center">
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-6">
              Ready to Save Time on Permit Research?
            </h2>
            <p className="text-xl text-white/90 mb-8">
              Join electrical contractors who are already using PermitBuddy to
              streamline their permit fee calculations.
            </p>
            <Link to="/register">
              <Button
                variant="secondary"
                size="lg"
                className="bg-white text-primary hover:bg-gray-50 px-8"
              >
                Get Started Today
              </Button>
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
